import { Navigate, Route, Routes } from "react-router-dom";
import Home from "./pages/home/home";
import Profile from "./pages/profile/profile";
import { ProfileProvider } from "./context/ProfileContext";
import "./App.css";
import WordPrompt from "./pages/word-prompt/WordPrompt";
import TeacherDashboard from "./pages/teacher-dashboard";
import MyCollectibles from "./pages/collectables/collectables";
import Leaderboard from "./pages/leaderboard/leaderboard";
import { useEffect, useRef, useState } from "react";
import { initialSetup } from "./services/backend/api";
import FunWithNumbers from "./pages/fun-with-numbers/FunWithNumbers";
import Login from "./pages/login/Login";
import StudentSignIn from "./pages/login/StudentSignIn";
import GuestSignIn from "./pages/login/GuestSignIn";
import AnimationPage from "./pages/animation/animation";
import { registerSW } from "virtual:pwa-register";
import SetupLoadingScreen from "./components/SetupLoadingScreen";

type DeferredInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

const INSTALL_PROMPT_DISMISSED_AT_KEY = "installPromptDismissedAt";
const INSTALL_PROMPT_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const SETUP_COMPLETE_KEY = "initialSetupCompleted";

function App() {
  const isMountedRef = useRef(true);
  const [isUpdateReady, setIsUpdateReady] = useState(false);
  const [applyUpdate, setApplyUpdate] = useState<
    ((reloadPage?: boolean) => Promise<void>) | null
  >(null);
  const [deferredInstallPrompt, setDeferredInstallPrompt] =
    useState<DeferredInstallPromptEvent | null>(null);
  const [isInstallPromptVisible, setIsInstallPromptVisible] = useState(false);
  const [isInstallPrompting, setIsInstallPrompting] = useState(false);
  const [isSetupLoading, setIsSetupLoading] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(SETUP_COMPLETE_KEY) !== "true";
  });
  const [setupProgress, setSetupProgress] = useState(8);
  const [setupMessage, setSetupMessage] = useState("Preparing local learning data...");

  const isRunningStandalone = () =>
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

  const rememberInstallDismissal = () => {
    localStorage.setItem(INSTALL_PROMPT_DISMISSED_AT_KEY, String(Date.now()));
  };

  useEffect(() => {
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        setIsUpdateReady(true);
      },
    });
    setApplyUpdate(() => updateSW);
  }, []);

  useEffect(() => {
    if (isRunningStandalone()) {
      return;
    }

    const lastDismissedAt = Number(
      localStorage.getItem(INSTALL_PROMPT_DISMISSED_AT_KEY) ?? "0"
    );
    const canShowInstallPrompt =
      !Number.isFinite(lastDismissedAt) ||
      Date.now() - lastDismissedAt > INSTALL_PROMPT_COOLDOWN_MS;

    const handleBeforeInstallPrompt = (event: Event) => {
      if (!canShowInstallPrompt || isRunningStandalone()) {
        return;
      }

      const deferredEvent = event as DeferredInstallPromptEvent;
      deferredEvent.preventDefault();
      setDeferredInstallPrompt(deferredEvent);
      setIsInstallPromptVisible(true);
    };

    const handleAppInstalled = () => {
      setIsInstallPromptVisible(false);
      setDeferredInstallPrompt(null);
      localStorage.removeItem(INSTALL_PROMPT_DISMISSED_AT_KEY);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstallNow = async () => {
    if (!deferredInstallPrompt) return;

    setIsInstallPrompting(true);
    try {
      await deferredInstallPrompt.prompt();
      const choice = await deferredInstallPrompt.userChoice;

      if (choice.outcome === "accepted") {
        setIsInstallPromptVisible(false);
        localStorage.removeItem(INSTALL_PROMPT_DISMISSED_AT_KEY);
      } else {
        rememberInstallDismissal();
        setIsInstallPromptVisible(false);
      }
    } finally {
      setDeferredInstallPrompt(null);
      setIsInstallPrompting(false);
    }
  };

  const handleInstallLater = () => {
    rememberInstallDismissal();
    setIsInstallPromptVisible(false);
    setDeferredInstallPrompt(null);
  };

  useEffect(() => {
    isMountedRef.current = true;
    const startedAt = Date.now();
    const setupMessages = [
      "Preparing local learning data...",
      "Checking saved words and profiles...",
      "Caching learning resources...",
      "Finalizing offline setup..."
    ];

    let useSimulatedProgress = true;
    const progressTimer = window.setInterval(() => {
      if (!useSimulatedProgress) return;
      const elapsedSeconds = (Date.now() - startedAt) / 1000;
      const simulatedProgress = Math.min(92, 8 + Math.round(elapsedSeconds * 4));
      const messageIndex = Math.min(
        setupMessages.length - 1,
        Math.floor(simulatedProgress / 28)
      );

      setSetupProgress((current) => Math.max(current, simulatedProgress));
      setSetupMessage(setupMessages[messageIndex]);
    }, 700);

    const handleProgressEvent = (e: Event) => {
      const customEvent = e as CustomEvent<{ progress?: number; message?: string }>;
      useSimulatedProgress = false;
      if (customEvent.detail) {
        if (customEvent.detail.progress !== undefined) {
          setSetupProgress(customEvent.detail.progress);
        }
        if (customEvent.detail.message !== undefined) {
          setSetupMessage(customEvent.detail.message);
        }
      }
    };
    window.addEventListener("setup:progress", handleProgressEvent);

    (async () => {
      console.log("[App] Initial setup starting...");
      try {
        await initialSetup();
        console.log("[App] Initial setup completed successfully.");
      } catch (error) {
        console.error("[App] Initial setup failed:", error);
      } finally {
        window.clearInterval(progressTimer);
        window.removeEventListener("setup:progress", handleProgressEvent);
        localStorage.setItem(SETUP_COMPLETE_KEY, "true");
      }

      if (!isMountedRef.current) return;
      console.log("[App] Transitioning from loading screen to app...");
      setSetupMessage("Setup complete.");
      setSetupProgress(100);
      window.setTimeout(() => {
        if (isMountedRef.current) {
          console.log("[App] Loading screen dismissed. App ready.");
          setIsSetupLoading(false);
        }
      }, 350);
    })();

    return () => {
      isMountedRef.current = false;
      window.clearInterval(progressTimer);
      window.removeEventListener("setup:progress", handleProgressEvent);
    };
  }, []);

  if (isSetupLoading) {
    return <SetupLoadingScreen progress={setupProgress} message={setupMessage} />;
  }
  return (
    <ProfileProvider>
      {isUpdateReady && (
        <div className="fixed left-1/2 top-3 z-[9999] w-[95%] max-w-xl -translate-x-1/2 rounded-xl border border-blue-300 bg-white/95 px-3 py-2 shadow-lg backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium text-blue-900">
              A new version is available.
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsUpdateReady(false)}
                className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700"
              >
                Later
              </button>
              <button
                type="button"
                onClick={() => {
                  void applyUpdate?.(true);
                }}
                className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white"
              >
                Update now
              </button>
            </div>
          </div>
        </div>
      )}

      {isInstallPromptVisible && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/35 p-4">
          <div className="w-full max-w-md rounded-2xl border border-emerald-200 bg-white p-5 shadow-xl">
            <h2 className="text-lg font-extrabold text-emerald-800">Install TG Tots</h2>
            <p className="mt-2 text-sm font-medium text-slate-700">
              Install this app for quick access and a smoother classroom experience.
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleInstallLater}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700"
              >
                Later
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleInstallNow();
                }}
                disabled={isInstallPrompting}
                className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
              >
                {isInstallPrompting ? "Preparing..." : "Install App"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/login/student" element={<StudentSignIn />} />
        <Route path="/login/guest" element={<GuestSignIn />} />
        <Route path="/home" element={<Home />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/word-prompt" element={<WordPrompt />} />
        <Route path="/teacher-dashboard" element={<TeacherDashboard />} />
        <Route path="/collectables" element={<MyCollectibles />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/fun-with-numbers" element={<FunWithNumbers />} />
        <Route path="/animation" element={<AnimationPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ProfileProvider>
  );
}

export default App;
